import { useEffect, useRef, useState } from 'react'
import classNames from 'classnames/bind'
import { useTranslation } from 'next-i18next'
import { useQuery } from 'react-query'
import Link from 'next/link'

// Libs
import { fetchProductRecommend } from '@/api/fetchProductRecommend'
import { useAppSelector } from '@/redux/hooks'
import useMedia from '@/hooks/useMedia'
import { useObserver } from '@/hooks/useObserver'
import { handleGtmViewItemList } from '@/methods/handleGtmEvent'
import { getVisitorId, REC_SOURCE } from '@/methods/recommendTracking'

// Components
import ProductCard from '@/components/shared/ProductCard'
import HotSectionTitle from '../utils/HotSectionTitle'

// Constants
import { GtmItemListName } from '@/constants/gtmEvent'
import { ME_V2_TAB } from '@/constants/meV2Tab'
import { QUERY_KEY } from '@/constants/queryKey'
import { ROUTE_PATH } from '@/constants/routePath'

// Styles
import styles from './style.module.scss'

const cx = classNames.bind(styles)

// 兩排：桌機 6 欄、平板 3 欄、手機 2 欄（手機三排）
const DESKTOP_LIMIT = 12
const MOBILE_LIMIT = 6

const GTM_LIST = { itemListName: GtmItemListName.RECOMMEND_FOR_YOU, itemListId: `recommend_${REC_SOURCE.FOR_YOU}`, useListForItems: true }

// 為你推薦：後端有個人化結果（source = for_you）才顯示。會員依購買紀錄、偏好分類與最近看過；
// 未登入的訪客依這台裝置最近看過的商品；都沒有時後端回熱門，這裡就不顯示
const ForYouProducts = () => {
  const { t } = useTranslation()
  const { isMobile, isTablet } = useMedia()
  const memberInfoId = useAppSelector((state) => state.member?.memberData?.memberInfoId)
  const limit = isMobile || isTablet ? MOBILE_LIMIT : DESKTOP_LIMIT
  // 只在瀏覽器取得（SSR 沒有 localStorage）
  const [visitorId, setVisitorId] = useState('')
  useEffect(() => setVisitorId(getVisitorId()), [])

  const { data } = useQuery(
    [QUERY_KEY.FETCH_PRODUCT_RECOMMEND, { type: 'for-you', memberInfoId, visitorId, limit }],
    () => fetchProductRecommend({ type: 'for-you', visitorId, limit }),
    { enabled: !!memberInfoId || !!visitorId, staleTime: 5 * 60 * 1000 },
  )
  const items = data?.source === 'for_you' ? data.items : []

  // 區塊第一次出現在畫面上時送一次 view_item_list
  const sentRef = useRef(false)
  const { observerRef } = useObserver({
    observerCallback: (entries) => {
      if (sentRef.current || !items.length || !entries.some((entry) => entry.isIntersecting)) return
      sentRef.current = true
      handleGtmViewItemList({ ...GTM_LIST, productInfoList: items })
    },
    dep: [items],
  })

  if (!items.length) return null

  return (
    <div className={cx('for-you-products')}>
      <HotSectionTitle>
        {t('Home_For_You_Title')}
        {/* 偏好分類在會員中心，訪客不顯示 */}
        {!!memberInfoId && (
          <Link href={`${ROUTE_PATH.ME}?tab=${ME_V2_TAB.PREFERENCE}`} className={cx('for-you-products__edit-button')}>
            {t('Home_For_You_Edit_Preference')}
          </Link>
        )}
      </HotSectionTitle>
      <div className={cx('for-you-products__list')}>
        {items.map((item) => (
          <ProductCard
            key={item.productId}
            {...(item as any)}
            observer={observerRef.current}
            gtmItemEventParams={GTM_LIST}
            recSource={REC_SOURCE.FOR_YOU}
          />
        ))}
      </div>
    </div>
  )
}

export default ForYouProducts
